import {
  Waves,
  Settings,
  Map,
  BarChart3,
  Activity,
  MessageCircle,
  Eye,
  CheckCircle,
} from 'lucide-react';

// Tutorial step categories
export const TUTORIAL_CATEGORIES = {
  GETTING_STARTED: 'getting_started',
  BASIC_FEATURES: 'basic_features',
  ADVANCED_FEATURES: 'advanced_features',
  DATA_ANALYSIS: 'data_analysis'
};

// Tutorial step definitions
export const TUTORIAL_STEPS = [
  {
    id: 'welcome',
    category: TUTORIAL_CATEGORIES.GETTING_STARTED,
    title: 'Welcome to Bluemvmt Digital Ocean',
    subtitle: 'Your comprehensive platform for ocean data analysis',
    description: 'Interactive ocean data visualization and analysis platform.',
    content: `Welcome to **Bluemvmt Digital Ocean** — explore ocean data on the map, tune the control panel, and chat with the assistant.

**You'll see:**
- How to move through time and choose datasources
- Where stations, layers, and currents appear on the map
- How the right column hosts the **assistant** and **exercise comms**

**Time:** about **3–5 minutes** (you can skip anytime). Use **Next** or the arrow keys; highlighted areas match each step.`,
    icon: Waves,
    target: null,
    position: 'center',
    actions: ['next'],
    duration: 30,
    category_color: 'blue'
  },

  {
    id: 'control-panel',
    category: TUTORIAL_CATEGORIES.BASIC_FEATURES,
    title: 'Master the Control Panel',
    subtitle: 'Your command center for ocean data exploration',
    description: 'Learn to navigate data controls, animation, and parameter selection.',
    content: `The Control Panel is your primary interface for controlling the oceanographic visualization:
  
  **🎯 Datasource Selection**
  Choose from 20+ BlueTide datasources:
  • Surface Vessel Data, Subsurface Vessel Data
  • Acoustic Data, Synthetic Acoustic Data
  • WMO Weather data (Wind, Air Pressure, Temperature)
  • High-Resolution Imaging and more
  
  **📊 Parameter Controls**
  Monitor various oceanographic parameters:
  • Current Speed & Direction (m/s, degrees)
  • Surface Elevation & Direction (m, degrees)  
  • Temperature, Salinity, Pressure
  • Wind Speed & Direction
  
  **⏰ Temporal Navigation**
  • Date/Time range selection (auto-discovered from available data)
  • Animation playback controls (play/pause/speed)
  • Frame-by-frame navigation
  • Loop modes (repeat, once, bounce)
  
  **📏 Depth Selection**
  When available for the selected datasource, choose measurement depths to analyze vertical ocean structure.
  
  **Try changing the datasource or selecting different parameters to see live updates.**`,
    icon: Settings,
    target: '[data-tutorial="control-panel"]',
    position: 'bottom',
    actions: ['prev', 'next'],
    highlight: true,
    duration: 45,
    tips: [
      'Use keyboard shortcuts: Space for play/pause, arrow keys for frame navigation',
      'Higher playback speeds help identify trends over longer time periods',
      'Different depths reveal stratified ocean structure'
    ]
  },

  {
    id: 'map-interaction',
    category: TUTORIAL_CATEGORIES.BASIC_FEATURES,
    title: 'Interactive Ocean Mapping',
    subtitle: 'Explore stations, currents, and environmental layers',
    description: 'Navigate the map, interact with monitoring stations, and customize layers.',
    content: `The interactive map provides comprehensive ocean visualization with multiple data layers and controls:
  
  **🗺️ Map Controls & Styles**
  • Satellite, Dark, Outdoors, Streets view modes
  • Globe projection with auto-rotation option
  • Zoom and pan controls with touch/mouse support
  • Global view and station zoom buttons
  
  **🎯 Monitoring Stations**
  • Colored markers represent data collection stations
  • Click stations to view detailed information
  • Station colors indicate data density/quality
  • Real ocean monitoring locations vs simulated data
  
  **🌬️ Wind Visualization**
  • **Live Wind Particles**: Real-time wind data visualization
  • **Wind Vectors**: Synthetic wind pattern display  
  • Adjustable particle count, speed, and fade settings
  • Color-coded wind speeds (light air to storm conditions)
  
  **🌊 Oceanographic Layers**
  • **Bathymetry**: Ocean depth visualization (-11,000m to surface)
  • **Sea Surface Temperature**: MODIS satellite data overlay
  • **Ocean Current Vectors**: Real-time flow direction and speed
  • **Ocean Base Layer**: ArcGIS oceanographic reference map
  
  **💡 Pro Tips:**
  • Enable globe auto-rotation for global exploration
  • Combine multiple layers for comprehensive analysis
  • Use wind particles for real-time atmospheric conditions`,
    icon: Map,
    target: '[data-tutorial="map-container"]',
    position: 'right',
    actions: ['prev', 'next'],
    highlight: true,
    duration: 60,
    tips: [
      'Right-click and drag to rotate the 3D globe view',
      'Station validation shows whether locations are on water vs land',
      'Wind particle density affects performance - adjust for your device'
    ]
  },

  {
    id: 'data-panels',
    category: TUTORIAL_CATEGORIES.DATA_ANALYSIS,
    title: 'Environmental Data Analysis',
    subtitle: 'Real-time monitoring and trend analysis',
    description: 'Understand environmental readings, time-series charts, and data quality.',
    content: `The Data Panels provide comprehensive environmental monitoring and analysis tools:

**📊 Environmental Data Dashboard**

Real-time sensor readings with quality indicators:

- **Temperature** — Water temperature in °F with depth correlation
- **Salinity** — Practical Salinity Units (PSU) for water density
- **Pressure** — Depth pressure in decibars (dbar)
- **Sensor Depth** — Current measurement depth in meters

Together, these readings describe the water column at the selected time and place. When you change **depth**, **area**, or **time** in the control panel, the context the assistant uses updates accordingly.

**📈 Time Series Analysis**

Interactive charts show temporal trends:

- **Current speed** — Flow velocity over time (m/s)
- **Sound speed** — Acoustic velocity changes (m/s); important for sonar and acoustic propagation
- **Surface elevation (SSH)** — Sea surface height patterns (m)
- **Temperature** — Thermal structure over time (°F)

Use the **timeline / playback** controls to move through frames: charts and tables in the right column often reflect the **current frame** so you can compare “now” vs earlier in the run.

**⚙️ Data Quality Assessment**

- **Real-time vs historical** indicators where shown
- **Connection status** (header and assistant) so you know if live API data is available
- **Timestamps** and station context when responses include them
- **Station validation** and reliability concepts when working from map selections

**🔧 Customization & Exploration**

- Adjust **date/time range** and **playback speed** to scan long runs quickly or inspect a short window
- Change **parameters** and **depth** in the control panel to focus the analysis
- **Refresh** data when you need a new pull from the source
- Layout is **responsive** — charts and controls adapt on smaller screens

**Advanced capabilities**

- **API-backed** answers when connected; local fallbacks when not
- **Multi-parameter** interpretation in richer assistant answers

---

**Where this shows up in the platform**

The highlighted area is the **right column** (below the yellow header):

- **Assistant** — your conversation with the Digital Ocean AI; control panel selections supply context so answers stay aligned with the scenario.
- **Exercise comms** — scenario radio transcript during playback-style exercises.

During this tour, the app may switch you to the assistant so the spotlight matches what the text describes.`,
    icon: BarChart3,
    target: '[data-tutorial="data-panels"]',
    position: 'left',
    actions: ['prev', 'next'],
    highlight: true,
    duration: 50,
    tips: [
      'Longer time ranges reveal seasonal patterns',
      'Temperature gradients indicate thermocline depth',
      'Sound speed affects sonar and acoustic equipment'
    ]
  },

  {
    id: 'output-module',
    category: TUTORIAL_CATEGORIES.DATA_ANALYSIS,
    title: 'Assistant & exercise comms',
    subtitle: 'Chat with the AI and follow scenario communications',
    description: 'Use the right column for conversational analysis and exercise transcripts.',
    content: `The right column is your **analysis and comms hub**:

**Assistant**
- Ask questions in plain language about currents, vessels, temperature, safety, and more
- Your **control panel** selections (datasource, date range, depth) inform each answer
- Expand the chat for a larger view when reviewing long replies

**Exercise comms**
- During scenario playback, switch to **Exercise comms** for the radio-style transcript
- Comms stay synced with the map timeline when the exercise layer is active

**Tips**
- Use **New chat** to start a fresh thread
- Playback may auto-switch you to Exercise comms when an exercise is running`,
    icon: Activity,
    target: '[data-tutorial="output-module"]',
    position: 'left',
    actions: ['prev', 'next'],
    highlight: true,
    duration: 40,
    tips: [
      'Ask the assistant to change date range or depth — the control panel can sync automatically',
      'Expand the assistant when you need more room for long analyses'
    ]
  },

  {
    id: 'chatbot',
    category: TUTORIAL_CATEGORIES.BASIC_FEATURES,
    title: 'Bluemvmt Digital Ocean Assistant',
    subtitle: 'Intelligent oceanographic analysis and insights',
    description: 'Master AI-powered analysis with natural language queries.',
    content: `Bluemvmt Digital Ocean is your expert oceanographic analyst, providing intelligent insights about marine conditions:
  
  **🤖 Natural Language Interface**
  Ask questions in plain English about:
  • Current oceanographic conditions
  • Weather and sea state analysis  
  • Safety assessments for maritime operations
  • Predictive forecasting and trend analysis
  • Equipment recommendations and operational guidance
  
  **📊 Context-Aware Analysis**
  Bluemvmt Digital Ocean automatically considers:
  • Your current parameter selections (depth, location, time)
  • Real-time environmental data
  • Historical trends and patterns
  • Model outputs and confidence levels
  • Data source quality and reliability
  
  **🔍 Specialized Capabilities**
  • **Marine Safety**: Risk assessments for vessel operations
  • **Environmental Analysis**: Temperature gradients, salinity fronts
  • **Operational Forecasting**: 6-12 hour condition predictions
  • **Research Support**: Academic collaboration features
  • **Data Interpretation**: Complex oceanographic phenomenon explanation
  
  **💬 Quick Query Examples**
  Try these conversation starters:
  • *"What are the current conditions?"*
  • *"Analyze wave patterns for small boat safety"*
  • *"Predict conditions for the next 6 hours"*
  • *"How does temperature vary with depth?"*
  • *"Is it safe for diving operations?"*
  • *"Compare today's data to historical averages"*
  
  **🎯 Smart Suggestions**
  • Quick-access buttons for common queries
  • Context-sensitive question prompts
  • Follow-up question recommendations
  • Parameter adjustment suggestions
  
  **⚡ Integration Features**
  • Responses automatically saved to Output Module
  • Real-time data incorporation
  • Cross-platform compatibility
  • Mobile-optimized interface`,
    icon: MessageCircle,
    target: '[data-tutorial="chatbot"]',
    position: 'left',
    actions: ['prev', 'next'],
    highlight: true,
    duration: 45,
    tips: [
      'Be specific about location and conditions for best results',
      'Ask follow-up questions to dive deeper into analysis',
      'Bluemvmt Digital Ocean learns from your current settings and selections'
    ]
  },

  {
    id: 'advanced-features',
    category: TUTORIAL_CATEGORIES.ADVANCED_FEATURES,
    title: 'Advanced Platform Features',
    subtitle: 'Power user capabilities and hidden features',
    description: 'Unlock advanced analysis tools and professional workflows.',
    content: `Discover powerful features for advanced oceanographic analysis and research:
  
  **🎯 Station Deep Analysis**
  Professional monitoring station analysis:
  • Click any station → "Analyze Station Data" for comprehensive reports
  • Multi-parameter correlation analysis
  • Data quality validation and reliability assessment
  • Historical trend analysis with statistical confidence
  • CSV export for external processing (research/reports)
  
  **🌍 Global Ocean Exploration**
  Advanced mapping and visualization:
  • **Globe Mode**: Worldwide ocean exploration with projection switching
  • **Auto-rotation**: Continuous global scanning mode
  • **Multi-scale Analysis**: Seamless zoom from global to local scales
  • **Custom Projections**: Mercator, orthographic, stereographic views
  
  **📊 Data Management & Quality Control**
  Professional data handling:
  • **Real-time Quality Monitoring**: Connection status and data freshness
  • **Data Validation**: Ocean vs land position verification
  • **Performance Optimization**: Adaptive loading for large datasets
  
  **🔧 Research & Collaboration Tools**
  Academic and professional features:
  • **Export Capabilities**: NetCDF, MATLAB, CSV formats for analysis
  • **API Documentation**: Programmatic access for researchers
  • **Collaborative Analysis**: Shared sessions and viewpoints
  
  **📱 Mobile & Accessibility**
  Cross-platform optimization:
  • **Touch Controls**: Optimized for tablets and smartphones
  • **Responsive Design**: Adaptive layouts for any screen size
  • **Gesture Support**: Pinch-zoom, swipe navigation
  • **Offline Capability**: Cached data for field operations
  
  **⚙️ Customization & Automation**
  Power user configuration:
  • **Custom Layer Combinations**: Save preferred visualization setups
  • **Automated Monitoring**: Set alerts for condition changes
  • **Batch Processing**: Analyze multiple time periods simultaneously
  • **Integration APIs**: Connect with external monitoring systems`,
    icon: Eye,
    target: null,
    position: 'center',
    actions: ['prev', 'next'],
    duration: 60,
    tips: [
      'Station validation ensures data quality in research applications',
      'Export data in academic formats for peer-reviewed publications',
      'Mobile optimization allows field data collection and analysis'
    ]
  },

  {
    id: 'completion',
    category: TUTORIAL_CATEGORIES.GETTING_STARTED,
    title: 'Tutorial Complete! 🎉',
    subtitle: 'You\'re ready for professional ocean data analysis',
    description: 'Summary of learned features and next steps for exploration.',
    content: `Congratulations! You've mastered the Bluemvmt Digital Ocean platform.
  
  **🎓 Skills Acquired:**
  ✅ **Control Panel Navigation** - Parameter selection, temporal controls, animation
  ✅ **Interactive Mapping** - Station analysis, layer management, global exploration
  ✅ **Data Analysis** - Environmental monitoring, time-series interpretation, quality assessment
  ✅ **AI-Powered Insights** - Natural language queries, predictive analysis, safety assessment
  ✅ **Advanced Features** - Export capabilities, research tools, mobile optimization
  
  **🚀 Next Steps for Exploration:**
  • **Explore Your Region**: Focus on local waters and monitoring stations
  • **Ask Complex Questions**: Use Bluemvmt Digital Ocean for advanced oceanographic analysis
  • **Compare Time Periods**: Analyze seasonal patterns and long-term trends
  • **Export for Research**: Generate data for academic or operational use
  
  **🔧 Professional Applications:**
  • **Marine Operations**: Safety assessments and route planning
  • **Research Projects**: Data analysis for scientific publications
  • **Environmental Monitoring**: Coastal and offshore condition tracking
  • **Education**: Interactive learning for marine science students
  • **Emergency Response**: Real-time conditions for maritime incidents
  
  **📚 Additional Resources:**
  • Open this tour anytime: **Settings** (gear) → **Start Tutorial**
  • Check the Documentation section for API details
  • Contact BlueMvmt Maritime Technology for research collaboration
  • Join the Bluemvmt Digital Ocean community for updates and support
  
  **Ready to dive deep into ocean data analysis! 🌊**
  
  *The ocean holds countless secrets - this platform helps you discover them.*`,
    icon: CheckCircle,
    target: null,
    position: 'center',
    actions: ['prev', 'complete', 'restart'],
    duration: 45,
    category_color: 'green'
  }
];

// Step management utilities
export class TutorialStepManager {
  constructor(steps = TUTORIAL_STEPS) {
    this.steps = steps;
    this.currentStep = 0;
    this.completedSteps = new Set();
    this.startTime = null;
    this.stepTimes = {};
  }

  // Navigation methods
  goToStep(stepIndex) {
    if (stepIndex >= 0 && stepIndex < this.steps.length) {
      this.markStepCompleted(this.currentStep);
      this.currentStep = stepIndex;
      return this.getCurrentStep();
    }
    return null;
  }

  nextStep() {
    return this.goToStep(this.currentStep + 1);
  }

  prevStep() {
    return this.goToStep(this.currentStep - 1);
  }

  // Step state management
  getCurrentStep() {
    return this.steps[this.currentStep];
  }

  markStepCompleted(stepIndex) {
    this.completedSteps.add(stepIndex);
    this.stepTimes[stepIndex] = Date.now();
  }

  isStepCompleted(stepIndex) {
    return this.completedSteps.has(stepIndex);
  }

  getProgress() {
    return {
      current: this.currentStep + 1,
      total: this.steps.length,
      percentage: Math.round(((this.currentStep + 1) / this.steps.length) * 100),
      completed: this.completedSteps.size
    };
  }

  // Filtering and search
  getStepsByCategory(category) {
    return this.steps.filter(step => step.category === category);
  }

  findStepById(id) {
    return this.steps.find(step => step.id === id);
  }

  getStepsWithTargets() {
    return this.steps.filter(step => step.target);
  }

  // Analytics and timing
  startTutorial() {
    this.startTime = Date.now();
    this.currentStep = 0;
    this.completedSteps.clear();
    this.stepTimes = {};
  }

  getTutorialDuration() {
    if (!this.startTime) return 0;
    return Date.now() - this.startTime;
  }

  getStepDuration(stepIndex) {
    return this.stepTimes[stepIndex] || 0;
  }

  // Validation and requirements
  canAdvanceToStep(stepIndex) {
    // Add custom logic for step prerequisites
    return stepIndex >= 0 && stepIndex < this.steps.length;
  }

  getRequiredActions(stepIndex) {
    const step = this.steps[stepIndex];
    return step?.actions || [];
  }

  // Export and import
  exportProgress() {
    return {
      currentStep: this.currentStep,
      completedSteps: Array.from(this.completedSteps),
      stepTimes: this.stepTimes,
      startTime: this.startTime,
      totalDuration: this.getTutorialDuration()
    };
  }

  importProgress(progressData) {
    this.currentStep = progressData.currentStep || 0;
    this.completedSteps = new Set(progressData.completedSteps || []);
    this.stepTimes = progressData.stepTimes || {};
    this.startTime = progressData.startTime || null;
  }
}

// Utility functions
export const getTutorialStepById = (id) => {
  return TUTORIAL_STEPS.find(step => step.id === id);
};

export const getTutorialCategories = () => {
  return Object.values(TUTORIAL_CATEGORIES);
};

export const getStepsByCategory = (category) => {
  return TUTORIAL_STEPS.filter(step => step.category === category);
};

export const getTotalEstimatedTime = () => {
  return TUTORIAL_STEPS.reduce((total, step) => total + (step.duration || 30), 0);
};

export const getStepTargets = () => {
  return TUTORIAL_STEPS
    .filter(step => step.target)
    .map(step => ({
      id: step.id,
      target: step.target,
      highlight: step.highlight
    }));
};

const tutorialStepsModule = {
  TUTORIAL_STEPS,
  TUTORIAL_CATEGORIES,
  TutorialStepManager,
  getTutorialStepById,
  getTutorialCategories,
  getStepsByCategory,
  getTotalEstimatedTime,
  getStepTargets
};

export default tutorialStepsModule;